import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Scissors, IndianRupee, CalendarDays, Phone, MessageCircle, FileText } from "lucide-react";
import { api, absoluteUrl } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function Share() {
  const { token } = useParams();
  const { t } = useI18n();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/quotes/share/${token}`).then((r) => setQ(r.data)).catch(() => setErr(true));
  }, [token]);

  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/quotes/share/${token}/pdf`;

  const waShare = () => {
    if (!q) return;
    const url = window.location.href;
    const msg = t("waMessage")
      .replace("{business}", q.business_name || "our unit")
      .replace("{price}", Math.round(q.final_price).toLocaleString("en-IN"))
      .replace("{date}", q.delivery_date)
      .replace("{url}", url);
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  if (err) return <div className="min-h-screen grid place-items-center text-muted-foreground">Quote not found</div>;
  if (!q) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-primary/8 via-background to-background" />
      <div className="relative max-w-md mx-auto px-5 py-10">
        <div className="flex items-center gap-2 mb-8">
          <span className="w-9 h-9 rounded-lg bg-primary text-primary-foreground grid place-items-center">
            <Scissors size={16} />
          </span>
          <div>
            <div className="font-heading font-bold">StitchQuote</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("clientQuote")}</div>
          </div>
        </div>

        <div className="sq-card p-6 space-y-6">
          {q.image_url && (
            <img src={absoluteUrl(q.image_url)} alt="" className="w-full max-h-64 object-contain rounded-lg bg-muted" />
          )}
          <div>
            <div className="sq-label">{q.garment_type}</div>
            <h1 className="text-2xl font-heading font-bold text-secondary mt-1">{q.design_name}</h1>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="rounded-xl bg-primary/8 border border-primary/20 p-5">
              <div className="sq-label flex items-center gap-1"><IndianRupee size={12} /> {t("priceLabel")}</div>
              <div className="sq-stat text-5xl text-primary mt-1">{inr(q.final_price)}</div>
            </div>
            <div className="rounded-xl bg-muted/40 p-5">
              <div className="sq-label flex items-center gap-1"><CalendarDays size={12} /> {t("deliveryBy")}</div>
              <div className="sq-stat text-2xl mt-1">{q.delivery_date}</div>
            </div>
          </div>

          <div className="border-t border-dashed border-border pt-4">
            <div className="sq-label">{t("from")}</div>
            <div className="font-semibold mt-1">{q.business_name || "—"}</div>
            {q.business_phone && (
              <a href={`tel:${q.business_phone}`} className="mt-1 text-sm text-primary flex items-center gap-1">
                <Phone size={12} /> {q.business_phone}
              </a>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 pt-2">
            <Button
              data-testid="share-whatsapp-btn"
              onClick={waShare}
              className="h-12 bg-success hover:bg-success/90 text-primary-foreground"
            >
              <MessageCircle size={16} className="mr-2" /> {t("shareWhatsapp")}
            </Button>
            <a
              data-testid="share-pdf-link"
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center h-12 rounded-md border border-border bg-surface hover:bg-muted text-sm font-semibold sq-tap"
            >
              <FileText size={16} className="mr-2" /> {t("downloadPdf")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
